import { useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import { doc, getFirestore, onSnapshot } from 'firebase/firestore';

type BriefLead = {
  id?: string;
  projectName?: string | null;
  unitType?: string | null;
  closePrimaryReason?: string | null;
  closeAnalysis?: { primaryReason?: string | null; interestedLocation?: string | null } | null;
  nextFollowUpDate?: unknown;
  appointment?: { status?: string | null; scheduledAt?: unknown } | null;
  promptAnswers?: Record<string, { value?: unknown } | null> | null;
  updatedAt?: unknown;
  lastActivityAt?: unknown;
};
type BriefActivity = {
  id?: string;
  type?: string | null;
  event?: string | null;
  callOutcome?: string | null;
  notes?: string[] | string | null;
  meetingPoints?: string[] | null;
  promptAnswers?: Record<string, unknown> | null;
  at?: unknown;
};
type Discussion = { date: string; kind: string; text: string };
type Highlight = { label: string; text: string };
type Brief = { wants: string[]; selectedUnit: string; facts: string; highlights: Highlight[]; nextStep: string; reason: string };
type SavedAiBrief = {
  kind: 'ai'; summary: string; wants: string; facts: string[];
  latest: string; questions: string[];
};
type SavedBriefRecord = {
  status?: string;
  brief?: SavedAiBrief;
  updatedAt?: unknown;
};

const dateOf = (value: unknown): Date | null => {
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  if (value && typeof value === 'object' && 'toDate' in value && typeof value.toDate === 'function') {
    const date = value.toDate();
    return date instanceof Date && !Number.isNaN(date.getTime()) ? date : null;
  }
  return null;
};
const shortDate = (date: Date) => date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
const notesOf = (value: string[] | string | null | undefined) =>
  (Array.isArray(value) ? value : value ? [value] : [])
    .filter((note): note is string => typeof note === 'string' && !!note.trim())
    .map((note) => note.trim().replace(/\s+/g, ' '));
const systemEvents = new Set([
  'rating', 'assign', 'whatsapp-sent', 'fb-lead', 'status', 'followup', 'project-change',
  'ref-by', 'website-request-received', 'website-request-completed', 'reception-check-in',
]);
const compact = (text: string) => text.length > 100 ? `${text.slice(0, 97).trimEnd()}…` : text;
const pointText = (entry: Discussion) => {
  const parts = entry.text.split(' · ').map((part) => part.trim()).filter(Boolean);
  if (parts.length <= 2) return compact(entry.text);
  const selected = parts.map((part, index) => ({ part, index, score: significance({ ...entry, text: part }) }))
    .sort((a, b) => b.score - a.score || b.index - a.index)
    .slice(0, 2)
    .sort((a, b) => a.index - b.index);
  return compact(selected.map(({ part }) => part).join(' · '));
};
const significance = (entry: Discussion) => {
  const text = entry.text.toLowerCase();
  return (entry.kind === 'Visit' ? 2 : 0)
    + (/\b[a-z]-?\d{3,4}\b/i.test(entry.text) ? 4 : 0)
    + (/budget|cash|price|lakh|payment|loan|emi|બજેટ/i.test(text) ? 3 : 0)
    + (/final|pasand|book|interested|location|plan|possession|પસંદ/i.test(text) ? 2 : 0)
    + (/visit|aav|avse|avis|sunday|monday|tomorrow|કાલે/i.test(text) ? 1 : 0)
    + (entry.text.length > 35 ? 1 : 0)
    - (entry.kind === 'Update' ? 2 : 0);
};

export function buildLeadBrief(lead: BriefLead, activities: BriefActivity[], statusLabel: string, today = new Date()): Brief {
  const ordered = [...activities].sort((a, b) => (dateOf(a.at)?.getTime() || 0) - (dateOf(b.at)?.getTime() || 0));
  const contacts = ordered.filter((a) => a.type === 'call' || a.type === 'visit');
  const visits = contacts.filter((a) => a.type === 'visit');
  const latestContact = contacts[contacts.length - 1];
  const discussions = ordered.flatMap((activity): Discussion[] => {
    const isAppt = String(activity.event || '').startsWith('appointment-');
    if (activity.type !== 'call' && activity.type !== 'visit' &&
      !(activity.type === 'internal' && (!systemEvents.has(String(activity.event || '')) || isAppt))) return [];
    if (activity.type === 'call' && /^(cnr|no-answer|no answer)$/i.test(String(activity.callOutcome || ''))) return [];
    const parts = Array.from(new Set([...notesOf(activity.notes), ...notesOf(activity.meetingPoints)]));
    if (!parts.length) return [];
    const kind = activity.type === 'visit' ? 'Visit' : activity.type === 'call' ? 'Call' : isAppt ? 'Appointment' : 'Update';
    const date = dateOf(activity.at);
    return [{ date: date ? shortDate(date) : 'Date unavailable', kind, text: parts.join(' · ') }];
  });
  const latestDiscussion = discussions[discussions.length - 1];
  const earlier = discussions.slice(0, -1)
    .map((entry, index) => ({ entry, index, score: significance(entry) }))
    .filter(({ score }) => score >= 2)
    .sort((a, b) => b.score - a.score || b.index - a.index)
    .slice(0, 2)
    .sort((a, b) => a.index - b.index);
  const highlights: Highlight[] = earlier.map(({ entry }) => ({
    label: entry.kind === 'Visit' ? 'Visit point' : entry.kind === 'Call' ? 'Call point' : entry.kind === 'Appointment' ? 'Appointment point' : 'Enquiry point',
    text: pointText(entry),
  }));
  if (latestDiscussion && !highlights.some(({ text }) => text === pointText(latestDiscussion))) {
    highlights.push({ label: `Latest ${latestDiscussion.kind.toLowerCase()} · ${latestDiscussion.date}`, text: pointText(latestDiscussion) });
  }
  const answerText = (answer: unknown) => {
    const value = answer && typeof answer === 'object' && 'value' in answer ? answer.value : answer;
    const text = Array.isArray(value) ? value.filter(Boolean).join(', ') : String(value ?? '').trim();
    return text;
  };
  const preference = (key: string, label: string) => {
    const text = answerText(lead.promptAnswers?.[key]);
    return text ? `${label}: ${text}` : null;
  };
  const selectedUnit = answerText(lead.promptAnswers?.legacy_unitChoice) ||
    ordered.map((activity) => answerText(activity.promptAnswers?.legacy_unitChoice)).filter(Boolean).pop() || '';
  const wants = [
    [lead.projectName, lead.unitType].filter(Boolean).join(' · '),
    [preference('legacy_budget', 'Budget fit'), preference('legacy_floorChoice', 'Floor')].filter(Boolean).join(' · '),
    String(lead.closeAnalysis?.interestedLocation || '').trim(),
  ].filter(Boolean);
  if (!wants.length && !selectedUnit) wants.push('Project and unit preference not recorded.');
  const closeReason = String(lead.closePrimaryReason || lead.closeAnalysis?.primaryReason || '').trim();
  const followUp = dateOf(lead.nextFollowUpDate);
  const appointmentAt = lead.appointment?.status === 'scheduled' ? dateOf(lead.appointment.scheduledAt) : null;
  const normalizedStatus = statusLabel.trim().toLowerCase();
  const facts = [
    statusLabel, `${visits.length} visit${visits.length === 1 ? '' : 's'}`,
    closeReason ? `Closed: ${closeReason}` : null,
    appointmentAt ? `Appointment ${shortDate(appointmentAt)}` : null,
    !appointmentAt && followUp ? `Follow-up ${shortDate(followUp)}` : null,
  ].filter(Boolean).join(' · ');

  let nextStep: string;
  let reason: string;
  if (normalizedStatus === 'closed') {
    nextStep = 'No routine sales follow-up';
    reason = closeReason.toLowerCase() === 'completed'
      ? 'Closed as Completed. This alone does not confirm a booking.'
      : closeReason ? `Recorded close reason: ${closeReason}. Reopen if the customer enquires again.` : 'The lead is closed; review the close reason before reopening.';
  } else if (appointmentAt) {
    nextStep = appointmentAt.getTime() < today.getTime() ? 'Record the appointment outcome' : `Confirm the ${shortDate(appointmentAt)} appointment`;
    reason = 'The appointment is still marked as scheduled.';
  } else if (normalizedStatus === 'appointment') {
    nextStep = 'Confirm and record the appointment time';
    reason = 'The lead is at Appointment stage, but no scheduled time is recorded.';
  } else if (normalizedStatus === 'site visited') {
    const noPlan = /\bno\s+plan(?:n|ning)?\b/i.test(latestDiscussion?.text || '');
    nextStep = noPlan ? 'Check whether the buying timeline has changed' : followUp ? `Follow up on ${shortDate(followUp)}` : 'Agree a next date after the visit';
    reason = noPlan ? 'The most recent recorded discussion says there is no current plan.' : 'Check the selected unit and latest customer plan before contacting them.';
  } else if (normalizedStatus === 'negotiation') {
    nextStep = 'Clarify the remaining concern and decision date';
    reason = 'The lead is at Negotiation stage. Check earlier discussions before reaching out.';
  } else if (normalizedStatus === 'reopen') {
    nextStep = 'Confirm current interest';
    reason = 'The lead was reopened, so earlier requirements may have changed.';
  } else if (latestContact?.type === 'call' && /^(cnr|no-answer|no answer)$/i.test(String(latestContact.callOutcome || ''))) {
    nextStep = 'Try a different contact time';
    reason = 'The most recent logged call was not answered.';
  } else if (followUp) {
    nextStep = followUp.getTime() < today.getTime() ? 'Contact the lead now' : `Contact the lead on ${shortDate(followUp)}`;
    reason = `A follow-up is recorded for ${shortDate(followUp)}. Review each discussion before calling.`;
  } else {
    nextStep = 'Confirm project, unit type and buying timeline';
    reason = 'No specific next action or follow-up date is recorded.';
  }
  return { wants, selectedUnit, facts, highlights, nextStep, reason };
}

export default function LeadBriefCard({ lead, activities, statusLabel, loading, historyError }: {
  lead: BriefLead; activities: BriefActivity[]; statusLabel: string; loading: boolean; historyError: boolean;
}) {
  const brief = buildLeadBrief(lead, activities, statusLabel);
  const [saved, setSaved] = useState<SavedBriefRecord | null>(null);
  const [checkingSaved, setCheckingSaved] = useState(false);
  useEffect(() => {
    setSaved(null);
    if (!lead.id) { setCheckingSaved(false); return undefined; }
    setCheckingSaved(true);
    return onSnapshot(doc(getFirestore(), 'presenterBriefByLead', lead.id), (snap) => {
      setSaved(snap.exists() ? snap.data() as SavedBriefRecord : null);
      setCheckingSaved(false);
    }, () => { setSaved(null); setCheckingSaved(false); });
  }, [lead.id]);
  const ai = saved?.status === 'ready' && saved.brief?.kind === 'ai' ? saved.brief : null;
  const generatedAt = dateOf(saved?.updatedAt);
  const latestChange = Math.max(
    dateOf(lead.updatedAt)?.getTime() || 0,
    dateOf(lead.lastActivityAt)?.getTime() || 0,
    ...activities.map((activity) => dateOf(activity.at)?.getTime() || 0)
  );
  const aiOlder = !!ai && !!generatedAt && latestChange > generatedAt.getTime() + 60000;
  const reading = !ai && (loading || checkingSaved);
  const rowSx = { display: 'grid', gridTemplateColumns: '78px minmax(0, 1fr)', gap: 1, alignItems: 'start' };
  const labelSx = { fontSize: 12, color: '#667085', lineHeight: 1.5 };
  const valueSx = { fontSize: 12.5, color: '#222a37', lineHeight: 1.5, overflowWrap: 'anywhere' };
  return (
    <Box component="section" aria-label="Lead brief" sx={{ mt: 1.5, p: { xs: 1.5, sm: 2 }, border: '1px solid #dce6f5', borderRadius: 2, bgcolor: '#f2f6fd' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1.5 }}>
        <AutoAwesomeOutlinedIcon sx={{ fontSize: 18, color: '#174ea6' }} />
        <Typography component="h3" sx={{ fontSize: 16, fontWeight: 800, color: '#17428b' }}>Brief</Typography>
        {ai && <Typography sx={{ ml: 'auto', fontSize: 11, fontWeight: 700, color: aiOlder ? '#9a4c10' : '#45618e' }}>
          Saved AI · {generatedAt ? shortDate(generatedAt) : 'Earlier'}{aiOlder ? ' · Older than latest update' : ''}
        </Typography>}
      </Box>
      {ai && <Typography sx={{ fontSize: 12.5, color: '#222a37', lineHeight: 1.5, mb: 1.25 }}>{ai.summary}</Typography>}
      <Box sx={{ display: 'grid', gap: 1.25 }}>
        <Box sx={rowSx}>
          <Typography sx={labelSx}>Wants</Typography>
          <Box sx={{ display: 'grid', gap: 0.5, minWidth: 0 }}>
            {reading ? <Typography sx={valueSx}>Checking saved brief and lead history…</Typography> : <>
              {(ai ? [ai.wants].filter(Boolean) : brief.wants).map((line, index) => <Typography key={`${line}-${index}`} sx={valueSx}>{line}</Typography>)}
              {brief.selectedUnit && (
                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, width: 'fit-content', maxWidth: '100%', px: 1, py: 0.35, borderRadius: 1, border: '1px solid #bed2f2', bgcolor: '#e5efff' }}>
                  <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#45618e' }}>Selected unit</Typography>
                  <Typography sx={{ fontSize: 12, fontWeight: 800, color: '#17428b', overflowWrap: 'anywhere' }}>{brief.selectedUnit}</Typography>
                </Box>
              )}
            </>}
          </Box>
        </Box>
        <Box sx={rowSx}>
          <Typography sx={labelSx}>Facts</Typography>
          <Typography sx={valueSx}>{reading ? 'Reading activity…' : ai ? ai.facts.join(' · ') || brief.facts : brief.facts}</Typography>
        </Box>
        <Box sx={rowSx}>
          <Typography sx={labelSx}>History</Typography>
          <Box sx={{ minWidth: 0 }}>
            {reading ? <Typography sx={valueSx}>Reading discussions…</Typography> : ai ? (
              <Typography sx={valueSx}>{ai.latest || 'Review the timeline for the latest discussion.'}</Typography>
            ) : brief.highlights.length ? (
              <Box sx={{ display: 'grid', gap: 0.85 }}>
                {brief.highlights.map((point, index) => (
                  <Box key={`${point.label}-${index}`} sx={{ minWidth: 0 }}>
                    <Typography sx={{ ...labelSx, fontSize: 11.5, fontWeight: 700 }}>{point.label}</Typography>
                    <Typography sx={{ ...valueSx, fontSize: 12 }}>{point.text}</Typography>
                  </Box>
                ))}
              </Box>
            ) : <Typography sx={valueSx}>No discussion notes recorded yet.</Typography>}
          </Box>
        </Box>
      </Box>
      <Typography sx={{ mt: 1.5, mb: 0.75, fontSize: 12.5, fontWeight: 750, color: '#454f61' }}>Next steps</Typography>
      <Box sx={{ bgcolor: '#fff', border: '1px solid #d9e4f6', borderRadius: 1.5, p: 1.25 }}>
        <Typography sx={{ fontSize: 13, fontWeight: 750, color: '#202835', lineHeight: 1.45 }}>
          {reading ? 'Reviewing history…' : ai ? ai.questions[0] || brief.nextStep : brief.nextStep}
        </Typography>
        {!reading && (ai ? ai.questions.slice(1) : [brief.reason]).map((line, index) => (
          <Typography key={`${line}-${index}`} sx={{ mt: 0.25, fontSize: 11.5, color: '#667085', lineHeight: 1.45 }}>{line}</Typography>
        ))}
      </Box>
      {aiOlder && <Typography sx={{ mt: 1, fontSize: 11.5, color: '#9a4c10' }}>Newer lead activity exists. Check the timeline before relying on this saved AI brief.</Typography>}
      {historyError && <Typography sx={{ mt: 1, fontSize: 11.5, color: '#9a4c10' }}>Activity history is unavailable; review the timeline before acting on this suggestion.</Typography>}
    </Box>
  );
}
